import { useEffect, useRef, useCallback } from 'react';
import { useIntl } from 'umi';

/**
 * 按钮配置项类型
 */
export interface ChartFootBtnConfig {
  /** 按钮唯一标识，用于 data-url 属性 */
  key: string;
  /** 按钮文本的国际化 ID */
  labelId: string;
  /** 自定义 data 属性，会合并到按钮的 data-* 属性中 */
  dataAttrs?: Record<string, string>;
}

/**
 * Hook 上下文类型，传递给 onClick 函数
 */
export interface ChartFootBtnContext {
  /** 点击的按钮 key */
  btnKey: string;
  /** 从按钮 data-* 属性中获取的所有参数（自动转换为 camelCase） */
  dataAttrs: Record<string, string | null>;
  /** 原始 filter */
  filter: any;
  /** 其他配置数据，完全由外部决定传入什么 */
  customData?: any;
}

/**
 * useChartFootBtn Hook 的参数类型
 */
export interface UseChartFootBtnOptions<T = any> {
  /** 基础筛选条件 */
  filter: any;
  /** 按钮配置列表 */
  buttons: ChartFootBtnConfig[];
  /** 点击处理函数，完全由外部决定如何处理点击事件 */
  onClick: (context: ChartFootBtnContext) => void;
  /** 自定义数据，会传递给 onClick 函数 */
  customData?: any;
}

/**
 * 图表底部按钮 Hook
 *
 * 用于生成图表底部的可点击链接按钮，并处理点击事件。
 * 所有的跳转逻辑完全由外部通过 onClick 函数决定。
 *
 * @example
 * ```tsx
 * const { getBottomClickBtn, divElementRef } = useChartFootBtn({
 *   filter,
 *   buttons: [
 *     {
 *       key: 'feeds',
 *       labelId: 'Click to go to [Feeds]',
 *       dataAttrs: { StartTime: 'start_time', EndTime: 'end_time' },
 *     },
 *     { key: 'key-opinions', labelId: 'Click to go to [Key Opinions]' },
 *   ],
 *   onClick: ({ btnKey, dataAttrs, filter, customData }) => {
 *     const params = { ...filter };
 *     if (customData?.by_type === 'exclude_low_invalid') {
 *       params.is_valid = [2, 1];
 *     }
 *     if (btnKey === 'feeds') {
 *       goStatsDetailAction({ path: '/opinion/Feeds/Feeds', params });
 *     }
 *   },
 *   customData: { tab, by_type },
 * });
 * ```
 */
export function useChartFootBtn<T = any>({
  filter,
  buttons,
  onClick,
  customData,
}: UseChartFootBtnOptions<T>) {
  const { formatMessage } = useIntl();
  const divElementRef = useRef<HTMLDivElement>(null);

  /**
   * 将 data 属性转换为 data-* 格式的字符串
   */
  function buildDataAttrsString(dataAttrs?: Record<string, string>) {
    if (!dataAttrs) return '';
    return Object.entries(dataAttrs)
      .map(([key, value]) => `data-${key}="${value}"`)
      .join(' ');
  }

  /**
   * 生成底部按钮 HTML 字符串
   * @param dataAttrs 默认的 data 属性，会与按钮配置的 dataAttrs 合并
   */
  const getBottomClickBtn = useCallback((dataAttrs: Record<string, string>) => {
    return buttons
      .map(btn => {
        const mergedAttrs = { ...dataAttrs, ...btn.dataAttrs };
        const attrsString = buildDataAttrsString(mergedAttrs);
        return `<div class="chart-common-formatter-links" data-url="${btn.key}" ${attrsString}>
            ${formatMessage({ id: btn.labelId })}
          </div>`;
      })
      .join('');
  }, [buttons, formatMessage]);

  useEffect(() => {
    function clickEventItem(event: any) {
      const { target } = event;
      const btnKey = target?.getAttribute('data-url');
      if (!btnKey) return;

      const btnConfig = buttons.find(btn => btn.key === btnKey);
      if (!btnConfig) return;

      // 收集所有 data-* 属性
      const dataAttrs: Record<string, string | null> = {};
      for (const attr of target.attributes) {
        if (attr.name.startsWith('data-')) {
          // 移除 data- 前缀
          let key = attr.name.slice(5);
          // 转换为 camelCase：
          // 1. 处理 snake_case: start_time → startTime
          // 2. 处理 kebab-case: start-time → startTime
          // 3. 首字母小写: StartTime → startTime
          key = key
            .replace(/_([a-z])/gi, (_: string, c: string) => c.toUpperCase())
            .replace(/-([a-z])/gi, (_: string, c: string) => c.toUpperCase())
            .replace(/^[A-Z]/, (c: string) => c.toLowerCase());
          dataAttrs[key] = attr.value;
        }
      }

      onClick({
        btnKey,
        dataAttrs,
        filter,
        customData,
      });
    }

    if (divElementRef.current) {
      divElementRef.current.addEventListener('click', clickEventItem);
    }

    return () => {
      if (divElementRef.current) {
        divElementRef.current.removeEventListener('click', clickEventItem);
      }
    };
  }, [filter, buttons, onClick, customData]);

  return {
    getBottomClickBtn,
    divElementRef,
  };
}
